import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Tooltip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  TextField,
  Grid,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  AddCircleOutline as AddCircleOutlineIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AutoAwesome as AutoAwesomeIcon,
  FileDownload as FileDownloadIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { apiClient } from '@/services/api';
import { showSuccessMessage, showErrorMessage } from '@/components/common/GlobalSnackbar';
import DepartmentIndicatorAIDialog from './DepartmentIndicatorAIDialog';
import DepartmentIndicatorSortDialog from './DepartmentIndicatorSortDialog';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

// 部门指标数据类型定义
interface DepartmentIndicator {
  id: number;
  project_id: number;
  level1_code: string;
  level1_name: string;
  level2_code: string;
  level2_name: string;
  level3_code: string;
  level3_name: string;
  indicator_value: string;
  value_description: string;
  created_at: string;
  updated_at: string;
}

interface DepartmentIndicatorFormData {
  level1_name: string;
  level2_name: string;
  level3_name: string;
  indicator_value: string;
  value_description: string;
}

// 年度主要任务数据类型
interface AnnualTask {
  id: number;
  project_id: number;
  task_name: string;
  main_content: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

// 可拖拽的任务行组件
interface SortableTaskRowProps {
  task: AnnualTask;
  index: number;
  onEdit: (task: AnnualTask) => void;
  onDelete: (task: AnnualTask) => void;
}

const SortableTaskRow: React.FC<SortableTaskRowProps> = ({ task, index, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      hover
      sx={{
        cursor: isDragging ? 'grabbing' : 'grab',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="small"
            {...attributes}
            {...listeners}
            sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
          >
            <DragIndicatorIcon fontSize="small" />
          </IconButton>
          <Typography variant="body2">{index + 1}</Typography>
        </Box>
      </TableCell>
      <TableCell>{task.task_name}</TableCell>
      <TableCell>{task.main_content}</TableCell>
      <TableCell align="right">
        <Tooltip title="编辑">
          <IconButton size="small" onClick={() => onEdit(task)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="删除">
          <IconButton size="small" onClick={() => onDelete(task)} color="error">
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

interface AnnualTaskFormData {
  task_name: string;
  main_content: string;
  sort_order: number;
}

const DepartmentIndicatorManager: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  
  // 部门指标相关状态
  const [indicators, setIndicators] = useState<DepartmentIndicator[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [project, setProject] = useState<any>(null);
  
  // 部门指标对话框状态
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [editingIndicator, setEditingIndicator] = useState<DepartmentIndicator | null>(null);
  const [deletingIndicator, setDeletingIndicator] = useState<DepartmentIndicator | null>(null);
  
  // 批量删除相关状态
  const [selectedIndicatorIds, setSelectedIndicatorIds] = useState<number[]>([]);
  const [batchDeleteConfirmOpen, setBatchDeleteConfirmOpen] = useState(false);
  const [isBatchDeleting, setIsBatchDeleting] = useState(false);
  
  // 导出相关状态
  const [isExporting, setIsExporting] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  
  // 排序相关状态
  const [sortDialogOpen, setSortDialogOpen] = useState(false);
  const [isSorting, setIsSorting] = useState(false);
  
  // 部门指标表单数据
  const [formData, setFormData] = useState<DepartmentIndicatorFormData>({
    level1_name: '',
    level2_name: '',
    level3_name: '',
    indicator_value: '',
    value_description: '',
  });

  // 年度主要任务相关状态
  const [tasks, setTasks] = useState<AnnualTask[]>([]);
  const [addTaskDialogOpen, setAddTaskDialogOpen] = useState(false);
  const [editTaskDialogOpen, setEditTaskDialogOpen] = useState(false);
  const [deleteTaskDialogOpen, setDeleteTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<AnnualTask | null>(null);
  const [deletingTask, setDeletingTask] = useState<AnnualTask | null>(null);
  
  // 年度任务表单数据
  const [taskFormData, setTaskFormData] = useState<AnnualTaskFormData>({
    task_name: '',
    main_content: '',
    sort_order: 0,
  });

  // 拖拽传感器
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // 项目元信息（可编辑）
  const [meta, setMeta] = useState<any>({
    department_name: '',
    unit_name: '',
    project_funds_total: '',
    government_budget_fund: '',
    fiscal_special_account_fund: '',
    unit_fund: '',
    project_expenditure: '',
    basic_expenditure: '',
    annual_target: '',
  });
  const [savingMeta, setSavingMeta] = useState(false);
  const [extractingMeta, setExtractingMeta] = useState(false);

  // 获取项目信息
  const fetchProject = useCallback(async () => {
    if (!projectId) return;
    
    try {
      const response = await apiClient.get(`/projects/${projectId}`);
      
      if (response.success && response.data) {
        setProject(response.data);
      } else {
        console.error('获取项目信息失败:', response.message);
      }
    } catch (err: any) {
      console.error('获取项目信息失败:', err);
    }
  }, [projectId]);

  // 获取项目绩效元信息（包含所有预算字段）
  const fetchProjectMeta = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await apiClient.get(`/projects/${projectId}/performance-meta`);
      if (response.success && response.data) {
        setMeta(response.data);
      }
    } catch (e) {
      console.error('获取项目元信息失败', e);
    }
  }, [projectId]);

  // 获取部门指标列表
  const fetchIndicators = useCallback(async () => {
    if (!projectId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.get(`/projects/${projectId}/department-indicators?page=1&page_size=1000`);
      
      if (response.success && response.data) {
        setIndicators(response.data);
        setError(null);
      } else {
        setError(response.message || '获取部门指标列表失败');
      }
    } catch (err: any) {
      console.error('获取部门指标列表失败:', err);
      setError(err.response?.data?.detail || '获取部门指标列表失败');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // 获取年度主要任务列表
  const fetchTasks = useCallback(async () => {
    if (!projectId) return;
    
    try {
      const response = await apiClient.get(`/projects/${projectId}/department-annual-tasks?page=1&page_size=1000`);
      
      if (response.success && response.data) {
        setTasks(response.data);
      } else {
        showErrorMessage(response.message || '获取年度任务列表失败');
      }
    } catch (err: any) {
      console.error('获取年度任务列表失败:', err);
      showErrorMessage(err.response?.data?.detail || '获取年度任务列表失败');
    }
  }, [projectId]);

  // 组件挂载时获取数据
  useEffect(() => {
    fetchProject();
    fetchProjectMeta();
    fetchIndicators();
    fetchTasks();
  }, [fetchProject, fetchProjectMeta, fetchIndicators, fetchTasks]);

  // 保存项目元信息
  const handleSaveMeta = async () => {
    if (!projectId) return;
    setSavingMeta(true);
    try {
      // 所有字段都保存到 performance-meta
      const metaPayload = {
        department_name: meta.department_name,
        unit_name: meta.unit_name,
        project_funds_total: meta.project_funds_total,
        annual_fund_total: meta.annual_fund_total,
        government_budget_fund: meta.government_budget_fund,
        fiscal_special_account_fund: meta.fiscal_special_account_fund,
        unit_fund: meta.unit_fund,
        project_expenditure: meta.project_expenditure,
        basic_expenditure: meta.basic_expenditure,
        annual_target: meta.annual_target,
      };
      const resp = await apiClient.put(`/projects/${projectId}/performance-meta`, metaPayload);
      if (!resp.success) throw new Error(resp.message || '保存项目元信息失败');
      
      showSuccessMessage('保存基本信息成功');
      // 重新获取项目元信息
      await fetchProjectMeta();
    } catch (e: any) {
      setError(e.message || '保存失败');
    } finally {
      setSavingMeta(false);
    }
  };

  // 提取项目元信息和年度主要任务
  const handleExtractMeta = async () => {
    if (!projectId) return;
    setExtractingMeta(true);
    try {
      const resp = await apiClient.post(`/projects/${projectId}/department-indicators/extract-meta`, {});
      if (resp.success && resp.data) {
        // 更新项目信息
        if (resp.data.project_info) {
          const updatedMeta = { ...meta };
          Object.keys(resp.data.project_info).forEach((key) => {
            if (resp.data.project_info[key]) {
              updatedMeta[key] = resp.data.project_info[key];
            }
          });
          setMeta(updatedMeta);
        }
        
        // 刷新任务列表
        await fetchTasks();
        
        const infoCount = resp.data.project_info ? Object.keys(resp.data.project_info).filter(k => resp.data.project_info[k]).length : 0;
        const taskCount = resp.data.annual_tasks ? resp.data.annual_tasks.length : 0;
        showSuccessMessage(`成功提取${infoCount}个基本信息字段和${taskCount}个年度任务`);
      } else if (!resp.success) {
        setError(resp.message || '提取失败');
      }
    } catch (e: any) {
      setError(e.message || '提取失败');
      showErrorMessage(e.message || '提取基本信息失败');
    } finally {
      setExtractingMeta(false);
    }
  };

  // ========== 部门指标相关函数 ==========
  
  // 处理添加指标
  const handleAddIndicator = async () => {
    if (!projectId) return;
    
    try {
      const payload = {
        project_id: parseInt(projectId),
        level1_name: formData.level1_name,
        level2_name: formData.level2_name,
        level3_name: formData.level3_name,
        indicator_value: formData.indicator_value,
        value_description: formData.value_description,
      };
      
      const response = await apiClient.post(`/projects/${projectId}/department-indicators`, payload);
      
      if (response.success) {
        showSuccessMessage('添加部门指标成功');
        setAddDialogOpen(false);
        resetForm();
        fetchIndicators();
      } else {
        setError(response.message || '创建部门指标失败');
      }
    } catch (err: any) {
      console.error('创建部门指标失败:', err);
      setError(err.response?.data?.detail || '创建部门指标失败');
    }
  };

  // 处理编辑指标
  const handleEditIndicator = async () => {
    if (!projectId || !editingIndicator) return;
    
    try {
      const payload = {
        level1_name: formData.level1_name,
        level2_name: formData.level2_name,
        level3_name: formData.level3_name,
        indicator_value: formData.indicator_value,
        value_description: formData.value_description,
      };
      
      const response = await apiClient.put(
        `/projects/${projectId}/department-indicators/${editingIndicator.id}`,
        payload
      );
      
      if (response.success) {
        showSuccessMessage('更新部门指标成功');
        setEditDialogOpen(false);
        setEditingIndicator(null);
        resetForm();
        fetchIndicators();
      } else {
        setError(response.message || '更新部门指标失败');
      }
    } catch (err: any) {
      console.error('更新部门指标失败:', err);
      setError(err.response?.data?.detail || '更新部门指标失败');
    }
  };

  // 处理删除指标
  const handleDeleteIndicator = async () => {
    if (!projectId || !deletingIndicator) return;
    
    try {
      const response = await apiClient.delete(
        `/projects/${projectId}/department-indicators/${deletingIndicator.id}`
      );
      
      if (response.success) {
        showSuccessMessage('删除部门指标成功');
        setDeleteDialogOpen(false);
        setDeletingIndicator(null);
        fetchIndicators();
      } else {
        setError(response.message || '删除部门指标失败');
      }
    } catch (err: any) {
      console.error('删除部门指标失败:', err);
      setError(err.response?.data?.detail || '删除部门指标失败');
    }
  };

  // 批量删除指标
  const handleConfirmBatchDelete = async () => {
    if (selectedIndicatorIds.length === 0 || !projectId) return;
    
    setIsBatchDeleting(true);
    setError(null);
    
    try {
      // 逐个删除
      for (const id of selectedIndicatorIds) {
        await apiClient.delete(`/projects/${projectId}/department-indicators/${id}`);
      }
      
      showSuccessMessage(`成功删除 ${selectedIndicatorIds.length} 个指标`);
      setSelectedIndicatorIds([]);
      fetchIndicators();
      setBatchDeleteConfirmOpen(false);
    } catch (err: any) {
      console.error("批量删除部门指标失败:", err);
      setError(err.message || '批量删除失败');
    } finally {
      setIsBatchDeleting(false);
    }
  };

  // ========== 导出功能 ==========
  
  // 导出Excel
  const handleExportExcel = async () => {
    if (!projectId) return;
    setIsExporting(true);
    try {
      const resp = await fetch(`/api/v1/projects/${projectId}/department-indicators/export-excel`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
        }
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || '导出失败');
      }
      
      // 从响应头中提取文件名
      const contentDisposition = resp.headers.get('Content-Disposition') || '';
      let filename = `项目_${projectId}_部门整体目标绩效表.xlsx`; // 默认文件名
      
      // 优先使用后端返回的文件名
      const filenameMatch = contentDisposition.match(/filename\*?=['"]?(?:UTF-\d['"]*)?([^;\r\n"']*)['"]?;?/i);
      if (filenameMatch && filenameMatch[1]) {
        filename = decodeURIComponent(filenameMatch[1]);
      }
      
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      showSuccessMessage('Excel导出成功');
    } catch (err: any) {
      console.error('导出Excel失败:', err);
      showErrorMessage(err.message || 'Excel导出失败');
    } finally {
      setIsExporting(false);
    }
  };

  // 导出Word
  const handleExportWord = async () => {
    if (!projectId) return;
    setIsExporting(true);
    try {
      const resp = await fetch(`/api/v1/projects/${projectId}/department-indicators/export-word`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
        }
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || '导出失败');
      }
      
      // 从响应头中提取文件名
      const contentDisposition = resp.headers.get('Content-Disposition') || '';
      let filename = `项目_${projectId}_部门整体目标绩效表.docx`; // 默认文件名
      
      // 优先使用后端返回的文件名
      const filenameMatch = contentDisposition.match(/filename\*?=['"]?(?:UTF-\d['"]*)?([^;\r\n"']*)['"]?;?/i);
      if (filenameMatch && filenameMatch[1]) {
        filename = decodeURIComponent(filenameMatch[1]);
      }
      
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      showSuccessMessage('Word导出成功');
    } catch (err: any) {
      console.error('导出Word失败:', err);
      showErrorMessage(err.message || 'Word导出失败');
    } finally {
      setIsExporting(false);
    }
  };

  // 打开/关闭导出菜单
  const openExportMenu = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const closeExportMenu = () => {
    setExportMenuAnchor(null);
  };

  // 打开排序对话框
  const handleOpenSortDialog = () => {
    setSortDialogOpen(true);
  };

  // 关闭排序对话框
  const handleCloseSortDialog = () => {
    setSortDialogOpen(false);
  };

  // 保存排序
  const handleSaveSort = async (newCodes: any[]) => {
    setIsSorting(true);
    try {
      const response = await apiClient.post(`/projects/${projectId}/department-indicators/reorder`, {
        newCodes
      });

      if (response.success) {
        setSortDialogOpen(false);
        // 刷新指标列表
        fetchIndicators();
      } else {
        setError(response.message || '排序保存失败');
      }
    } catch (err: any) {
      console.error('排序保存失败:', err);
      setError(err.response?.data?.detail || '排序保存失败');
    } finally {
      setIsSorting(false);
    }
  };

  // 一级指标排序顺序
  const getLevel1Order = (level1Name: string) => {
    const order: Record<string, number> = {
      '投入管理指标': 1,
      '产出指标': 2,
      '效益管理指标': 3,
    };
    return order[level1Name] || 999;
  };

  // 二级指标排序顺序（固定）
  const getLevel2Order = (level2Name: string) => {
    const order: Record<string, number> = {
      // 投入管理指标
      '工作目标管理': 1,
      '预算和财务管理': 2,
      '绩效管理': 3,
      // 产出指标
      '重点工作完成情况': 1,
      '履职目标实现': 2,
      // 效益管理指标
      '履职效益': 1,
      '满意度': 2,
    };
    return order[level2Name] || 999;
  };

  // 获取排序后的指标列表
  const sortedIndicators = useMemo(() => {
    return [...indicators].sort((a, b) => {
      // 1. 先按一级指标顺序
      const level1OrderA = getLevel1Order(a.level1_name);
      const level1OrderB = getLevel1Order(b.level1_name);
      if (level1OrderA !== level1OrderB) {
        return level1OrderA - level1OrderB;
      }
      
      // 2. 再按二级指标固定顺序
      const level2OrderA = getLevel2Order(a.level2_name);
      const level2OrderB = getLevel2Order(b.level2_name);
      if (level2OrderA !== level2OrderB) {
        return level2OrderA - level2OrderB;
      }
      
      // 3. 最后按三级编码排序
      return (a.level3_code || '').localeCompare(b.level3_code || '');
    });
  }, [indicators]);

  // 重置表单
  const resetForm = () => {
    setFormData({
      level1_name: '',
      level2_name: '',
      level3_name: '',
      indicator_value: '',
      value_description: '',
    });
  };

  // 打开编辑对话框
  const openEditDialog = (indicator: DepartmentIndicator) => {
    setEditingIndicator(indicator);
    setFormData({
      level1_name: indicator.level1_name,
      level2_name: indicator.level2_name,
      level3_name: indicator.level3_name,
      indicator_value: indicator.indicator_value,
      value_description: indicator.value_description,
    });
    setEditDialogOpen(true);
  };

  // 打开删除对话框
  const openDeleteDialog = (indicator: DepartmentIndicator) => {
    setDeletingIndicator(indicator);
    setDeleteDialogOpen(true);
  };

  // 批量选择
  const handleSelectIndicatorForBatchDelete = (indicatorId: number, selected: boolean) => {
    setSelectedIndicatorIds(prev => {
      if (selected) {
        return [...prev, indicatorId];
      } else {
        return prev.filter(id => id !== indicatorId);
      }
    });
  };

  const handleSelectAllIndicatorsForBatchDelete = (selectAll: boolean) => {
    if (selectAll) {
      setSelectedIndicatorIds(indicators.map(ind => ind.id));
    } else {
      setSelectedIndicatorIds([]);
    }
  };

  const handleOpenBatchDeleteDialog = () => {
    if (selectedIndicatorIds.length === 0) {
      setError('请先选择要删除的部门指标');
      return;
    }
    setBatchDeleteConfirmOpen(true);
  };

  // ========== 年度主要任务相关函数 ==========
  
  // 重置任务表单
  const resetTaskForm = () => {
    setTaskFormData({
      task_name: '',
      main_content: '',
      sort_order: 0,
    });
  };

  // 处理添加任务
  const handleAddTask = async () => {
    if (!projectId) return;
    
    try {
      const payload = {
        project_id: parseInt(projectId),
        task_name: taskFormData.task_name,
        main_content: taskFormData.main_content,
        sort_order: taskFormData.sort_order,
      };
      
      const response = await apiClient.post(`/projects/${projectId}/department-annual-tasks`, payload);
      
      if (response.success) {
        showSuccessMessage('添加年度任务成功');
        setAddTaskDialogOpen(false);
        resetTaskForm();
        fetchTasks();
      } else {
        showErrorMessage(response.message || '创建年度任务失败');
      }
    } catch (err: any) {
      console.error('创建年度任务失败:', err);
      showErrorMessage(err.response?.data?.detail || '创建年度任务失败');
    }
  };

  // 处理编辑任务
  const handleEditTask = async () => {
    if (!projectId || !editingTask) return;
    
    try {
      const payload = {
        task_name: taskFormData.task_name,
        main_content: taskFormData.main_content,
        sort_order: taskFormData.sort_order,
      };
      
      const response = await apiClient.put(
        `/projects/${projectId}/department-annual-tasks/${editingTask.id}`,
        payload
      );
      
      if (response.success) {
        showSuccessMessage('更新年度任务成功');
        setEditTaskDialogOpen(false);
        setEditingTask(null);
        resetTaskForm();
        fetchTasks();
      } else {
        showErrorMessage(response.message || '更新年度任务失败');
      }
    } catch (err: any) {
      console.error('更新年度任务失败:', err);
      showErrorMessage(err.response?.data?.detail || '更新年度任务失败');
    }
  };

  // 处理删除任务
  const handleDeleteTask = async () => {
    if (!projectId || !deletingTask) return;
    
    try {
      const response = await apiClient.delete(
        `/projects/${projectId}/department-annual-tasks/${deletingTask.id}`
      );
      
      if (response.success) {
        showSuccessMessage('删除年度任务成功');
        setDeleteTaskDialogOpen(false);
        setDeletingTask(null);
        fetchTasks();
      } else {
        showErrorMessage(response.message || '删除年度任务失败');
      }
    } catch (err: any) {
      console.error('删除年度任务失败:', err);
      showErrorMessage(err.response?.data?.detail || '删除年度任务失败');
    }
  };

  // 打开编辑任务对话框
  const openEditTaskDialog = (task: AnnualTask) => {
    setEditingTask(task);
    setTaskFormData({
      task_name: task.task_name,
      main_content: task.main_content,
      sort_order: task.sort_order,
    });
    setEditTaskDialogOpen(true);
  };

  // 打开删除任务对话框
  const openDeleteTaskDialog = (task: AnnualTask) => {
    setDeletingTask(task);
    setDeleteTaskDialogOpen(true);
  };

  // 处理任务拖拽排序
  const handleTaskDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      return;
    }

    if (!projectId) return;

    // 获取排序后的任务列表
    const sortedTasks = [...tasks].sort((a, b) => a.sort_order - b.sort_order);
    const oldIndex = sortedTasks.findIndex((task) => task.id === active.id);
    const newIndex = sortedTasks.findIndex((task) => task.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // 重新排序
    const [movedTask] = sortedTasks.splice(oldIndex, 1);
    sortedTasks.splice(newIndex, 0, movedTask);

    // 更新本地状态（乐观更新）
    const updatedTasks = sortedTasks.map((task, index) => ({
      ...task,
      sort_order: index,
    }));
    setTasks(updatedTasks);

    // 调用API更新排序
    try {
      const taskOrders = updatedTasks.map((task, index) => ({
        id: task.id,
        sort_order: index,
      }));

      const response = await apiClient.post(
        `/projects/${projectId}/department-annual-tasks/reorder`,
        { task_orders: taskOrders }
      );

      if (response.success) {
        showSuccessMessage('任务排序更新成功');
        // 重新获取任务列表以确保数据同步
        fetchTasks();
      } else {
        // 如果失败，恢复原状态
        setTasks(tasks);
        showErrorMessage(response.message || '更新排序失败');
      }
    } catch (err: any) {
      console.error('更新任务排序失败:', err);
      // 恢复原状态
      setTasks(tasks);
      showErrorMessage(err.response?.data?.detail || '更新排序失败');
    }
  };

  // 处理AI推荐完成
  const handleAIRecommendationComplete = async (result: any) => {
    if (!projectId || !result?.data?.recommendedIndicators) return;
    
    try {
      // 批量创建推荐的部门指标
      const indicatorsToCreate = result.data.recommendedIndicators.map((indicator: any) => ({
        project_id: parseInt(projectId),
        level1_name: indicator.level1_name || '',
        level2_name: indicator.level2_name || '',
        level3_name: indicator.level3_name || '',
        indicator_value: String(indicator.indicator_value || ''),
        value_description: indicator.value_description || '',
      }));

      // 使用批量创建接口
      const response = await apiClient.post(`/projects/${projectId}/department-indicators/batch`, {
        indicators: indicatorsToCreate
      });

      if (response.success) {
        showSuccessMessage(`成功批量创建 ${indicatorsToCreate.length} 个部门指标`);
        fetchIndicators();
      } else {
        setError(response.message || '批量创建部门指标失败');
      }
    } catch (err: any) {
      console.error('批量创建部门指标失败:', err);
      setError(err.response?.data?.detail || '批量创建部门指标失败');
    }
  };

  return (
    <>
      <Paper sx={{ p: 3 }}>
        {/* 页面标题 */}
        <Typography variant="h5" component="h1" sx={{ mb: 3 }}>
          部门指标管理
        </Typography>

        {/* 错误提示 */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* 加载状态 */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {/* 顶部：基本信息（可编辑） */}
        <Paper sx={{ p: 1.5, mb: 2 }} variant="outlined">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>基本信息</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="outlined" onClick={handleExtractMeta} disabled={extractingMeta}>
                {extractingMeta ? '提取中...' : '提取基本信息'}
              </Button>
              <Button size="small" variant="contained" onClick={handleSaveMeta} disabled={savingMeta}>
                {savingMeta ? '保存中...' : '保存基本信息'}
              </Button>
            </Box>
          </Box>

          <TableContainer>
            <Table size="small" sx={{ border: '1px solid', borderColor: 'divider' }}>
              <TableBody>
                {/* 1. 部门（单位）名称 */}
                <TableRow>
                  <TableCell sx={{ width: '15%', bgcolor: 'grey.100', fontWeight: 'bold', borderRight: '1px solid', borderColor: 'divider' }}>
                    部门（单位）名称
                  </TableCell>
                  <TableCell colSpan={3}>
                    <TextField 
                      fullWidth 
                      size="small" 
                      variant="standard"
                      value={meta.department_name || ''}
                      onChange={(e) => setMeta({ ...meta, department_name: e.target.value })}
                      placeholder="请输入部门名称"
                    />
                  </TableCell>
                </TableRow>

                {/* 2. 年度履职目标 */}
                <TableRow>
                  <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 'bold', borderRight: '1px solid', borderColor: 'divider' }}>
                    年度履职目标
                  </TableCell>
                  <TableCell colSpan={3}>
                    <TextField 
                      fullWidth 
                      size="small" 
                      variant="standard"
                      multiline
                      rows={3}
                      value={meta.annual_target || ''}
                      onChange={(e) => setMeta({ ...meta, annual_target: e.target.value })}
                      placeholder="请输入年度履职目标"
                    />
                  </TableCell>
                </TableRow>

                {/* 3. 年度主要任务 */}
                <TableRow>
                  <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 'bold', verticalAlign: 'top', borderRight: '1px solid', borderColor: 'divider' }}>
                    年度主要任务
                  </TableCell>
                  <TableCell colSpan={3} sx={{ p: 0 }}>
                    <Box sx={{ p: 1, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={() => setAddTaskDialogOpen(true)}
                      >
                        添加任务
                      </Button>
                    </Box>
                    {tasks.length > 0 ? (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleTaskDragEnd}
                      >
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ width: '8%', bgcolor: 'grey.50', fontWeight: 'bold' }}>序号</TableCell>
                              <TableCell sx={{ width: '27%', bgcolor: 'grey.50', fontWeight: 'bold' }}>任务名称</TableCell>
                              <TableCell sx={{ bgcolor: 'grey.50', fontWeight: 'bold' }}>主要内容</TableCell>
                              <TableCell sx={{ width: '12%', bgcolor: 'grey.50', fontWeight: 'bold', textAlign: 'right' }}>操作</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <SortableContext
                              items={tasks.sort((a, b) => a.sort_order - b.sort_order).map((task) => task.id)}
                              strategy={verticalListSortingStrategy}
                            >
                              {tasks.sort((a, b) => a.sort_order - b.sort_order).map((task, index) => (
                                <SortableTaskRow
                                  key={task.id}
                                  task={task}
                                  index={index}
                                  onEdit={openEditTaskDialog}
                                  onDelete={openDeleteTaskDialog}
                                />
                              ))}
                            </SortableContext>
                          </TableBody>
                        </Table>
                      </DndContext>
                    ) : (
                      <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                        暂无年度主要任务，请点击"添加任务"按钮或使用"提取基本信息"功能
                      </Box>
                    )}
                  </TableCell>
                </TableRow>

                {/* 4. 资金情况 */}
                <TableRow>
                  <TableCell rowSpan={7} sx={{ bgcolor: 'grey.100', fontWeight: 'bold', verticalAlign: 'middle', borderRight: '1px solid', borderColor: 'divider' }}>
                    资金情况
                  </TableCell>
                  <TableCell colSpan={2} sx={{ width: '35%', bgcolor: 'grey.50' }}>部门预算总额（万元）</TableCell>
                  <TableCell>
                    <TextField 
                      fullWidth 
                      size="small" 
                      variant="standard"
                      value={meta.project_funds_total || ''}
                      onChange={(e) => setMeta({ ...meta, project_funds_total: e.target.value })}
                      placeholder="如：1556"
                    />
                  </TableCell>
                </TableRow>
                
                {/* 资金来源 */}
                <TableRow>
                  <TableCell rowSpan={3} sx={{ width: '15%', bgcolor: 'grey.100', fontWeight: 'bold', verticalAlign: 'middle', borderRight: '1px solid', borderColor: 'divider' }}>
                    1、资金来源：
                  </TableCell>
                  <TableCell sx={{ width: '20%' }}>（1）政府预算资金</TableCell>
                  <TableCell>
                    <TextField 
                      fullWidth 
                      size="small" 
                      variant="standard"
                      value={meta.government_budget_fund || ''}
                      onChange={(e) => setMeta({ ...meta, government_budget_fund: e.target.value })}
                      placeholder="如：777"
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>（2）财政专户管理资金</TableCell>
                  <TableCell>
                    <TextField 
                      fullWidth 
                      size="small" 
                      variant="standard"
                      value={meta.fiscal_special_account_fund || ''}
                      onChange={(e) => setMeta({ ...meta, fiscal_special_account_fund: e.target.value })}
                      placeholder="如：222"
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>（3）单位资金</TableCell>
                  <TableCell>
                    <TextField 
                      fullWidth 
                      size="small" 
                      variant="standard"
                      value={meta.unit_fund || ''}
                      onChange={(e) => setMeta({ ...meta, unit_fund: e.target.value })}
                      placeholder="如：100"
                    />
                  </TableCell>
                </TableRow>

                {/* 资金结构 */}
                <TableRow>
                  <TableCell rowSpan={2} sx={{ bgcolor: 'grey.100', fontWeight: 'bold', verticalAlign: 'middle', borderRight: '1px solid', borderColor: 'divider' }}>
                    2、资金结构：
                  </TableCell>
                  <TableCell>（1）基本支出</TableCell>
                  <TableCell>
                    <TextField 
                      fullWidth 
                      size="small" 
                      variant="standard"
                      value={meta.basic_expenditure || ''}
                      onChange={(e) => setMeta({ ...meta, basic_expenditure: e.target.value })}
                      placeholder="如：221"
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>（2）项目支出</TableCell>
                  <TableCell>
                    <TextField 
                      fullWidth 
                      size="small" 
                      variant="standard"
                      value={meta.project_expenditure || ''}
                      onChange={(e) => setMeta({ ...meta, project_expenditure: e.target.value })}
                      placeholder="如：1112"
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* 部门指标部分 */}
        <Box sx={{ mb: 2 }}>
          {/* 部门指标标题和操作按钮 */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              部门指标 ({indicators.length})
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => setAddDialogOpen(true)}
              >
                添加指标
              </Button>
              {indicators.length === 0 && (
                <Button
                  variant="outlined"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={() => setAiDialogOpen(true)}
                >
                  AI推荐指标
                </Button>
              )}
            <Button
              variant="outlined"
              startIcon={<TuneIcon />}
              onClick={handleOpenSortDialog}
              disabled={isSorting}
            >
              指标排序
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={openExportMenu}
              disabled={isExporting}
            >
              导出
            </Button>
            <Menu anchorEl={exportMenuAnchor} open={Boolean(exportMenuAnchor)} onClose={closeExportMenu}>
              <MenuItem onClick={() => { closeExportMenu(); handleExportExcel(); }} disabled={isExporting}>
                导出为 Excel (.xlsx)
              </MenuItem>
              <MenuItem onClick={() => { closeExportMenu(); handleExportWord(); }} disabled={isExporting}>
                导出为 Word (.docx)
              </MenuItem>
            </Menu>
            {selectedIndicatorIds.length > 0 && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleOpenBatchDeleteDialog}
                >
                  批量删除 ({selectedIndicatorIds.length})
                </Button>
              )}
            </Stack>
          </Box>

        {/* 指标表格 */}
        {!loading && (
          <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead sx={{ backgroundColor: 'action.hover' }}>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIndicatorIds.length === indicators.length && indicators.length > 0}
                        indeterminate={selectedIndicatorIds.length > 0 && selectedIndicatorIds.length < indicators.length}
                        onChange={(e) => handleSelectAllIndicatorsForBatchDelete(e.target.checked)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>序号</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>一级指标</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>二级指标</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>三级指标</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>指标值</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>指标值说明</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedIndicators.map((indicator, index) => (
                    <TableRow key={indicator.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIndicatorIds.includes(indicator.id)}
                          onChange={(e) => handleSelectIndicatorForBatchDelete(indicator.id, e.target.checked)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{indicator.level1_name}</TableCell>
                      <TableCell>{indicator.level2_name}</TableCell>
                      <TableCell>{indicator.level3_name || ''}</TableCell>
                      <TableCell>{indicator.indicator_value}</TableCell>
                      <TableCell>{indicator.value_description}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="编辑">
                          <IconButton size="small" onClick={() => openEditDialog(indicator)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="删除">
                          <IconButton size="small" onClick={() => openDeleteDialog(indicator)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
          </TableContainer>
        )}

        {/* 空状态 */}
        {!loading && indicators.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              暂无部门指标数据
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>

    {/* ========== 部门指标对话框 ========== */}
      
      {/* 添加指标对话框 */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>添加部门指标</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="一级指标"
                value={formData.level1_name}
                onChange={(e) => setFormData({ ...formData, level1_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="二级指标"
                value={formData.level2_name}
                onChange={(e) => setFormData({ ...formData, level2_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="三级指标"
                value={formData.level3_name}
                onChange={(e) => setFormData({ ...formData, level3_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="指标值"
                value={formData.indicator_value}
                onChange={(e) => setFormData({ ...formData, indicator_value: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="指标值说明"
                multiline
                rows={3}
                value={formData.value_description}
                onChange={(e) => setFormData({ ...formData, value_description: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>取消</Button>
          <Button onClick={handleAddIndicator} variant="contained">确定</Button>
        </DialogActions>
      </Dialog>

      {/* 编辑指标对话框 */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>编辑部门指标</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="一级指标"
                value={formData.level1_name}
                onChange={(e) => setFormData({ ...formData, level1_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="二级指标"
                value={formData.level2_name}
                onChange={(e) => setFormData({ ...formData, level2_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="三级指标"
                value={formData.level3_name}
                onChange={(e) => setFormData({ ...formData, level3_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="指标值"
                value={formData.indicator_value}
                onChange={(e) => setFormData({ ...formData, indicator_value: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="指标值说明"
                multiline
                rows={3}
                value={formData.value_description}
                onChange={(e) => setFormData({ ...formData, value_description: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>取消</Button>
          <Button onClick={handleEditIndicator} variant="contained">确定</Button>
        </DialogActions>
      </Dialog>

      {/* 删除指标确认对话框 */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>确认删除</DialogTitle>
        <DialogContent>
          <DialogContentText>
            确定要删除这个部门指标吗？此操作不可撤销。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>取消</Button>
          <Button onClick={handleDeleteIndicator} variant="contained" color="error">
            删除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 批量删除确认对话框 */}
      <Dialog open={batchDeleteConfirmOpen} onClose={() => setBatchDeleteConfirmOpen(false)}>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <DeleteIcon color="error" />
            <Typography variant="h6">确认批量删除部门指标</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Typography variant="body1" gutterBottom>
              此操作不可恢复，确定要删除选中的 {selectedIndicatorIds.length} 个部门指标吗？
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchDeleteConfirmOpen(false)} disabled={isBatchDeleting}>
            取消
          </Button>
          <Button 
            onClick={handleConfirmBatchDelete} 
            variant="contained" 
            color="error"
            disabled={isBatchDeleting}
            startIcon={isBatchDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {isBatchDeleting ? '删除中...' : '确认删除'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========== 年度任务对话框 ========== */}
      
      {/* 添加任务对话框 */}
      <Dialog open={addTaskDialogOpen} onClose={() => setAddTaskDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>添加年度主要任务</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="任务名称"
                value={taskFormData.task_name}
                onChange={(e) => setTaskFormData({ ...taskFormData, task_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="主要内容"
                multiline
                rows={4}
                value={taskFormData.main_content}
                onChange={(e) => setTaskFormData({ ...taskFormData, main_content: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="排序"
                type="number"
                value={taskFormData.sort_order}
                onChange={(e) => setTaskFormData({ ...taskFormData, sort_order: parseInt(e.target.value) || 0 })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddTaskDialogOpen(false)}>取消</Button>
          <Button onClick={handleAddTask} variant="contained">确定</Button>
        </DialogActions>
      </Dialog>

      {/* 编辑任务对话框 */}
      <Dialog open={editTaskDialogOpen} onClose={() => setEditTaskDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>编辑年度主要任务</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="任务名称"
                value={taskFormData.task_name}
                onChange={(e) => setTaskFormData({ ...taskFormData, task_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="主要内容"
                multiline
                rows={4}
                value={taskFormData.main_content}
                onChange={(e) => setTaskFormData({ ...taskFormData, main_content: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="排序"
                type="number"
                value={taskFormData.sort_order}
                onChange={(e) => setTaskFormData({ ...taskFormData, sort_order: parseInt(e.target.value) || 0 })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTaskDialogOpen(false)}>取消</Button>
          <Button onClick={handleEditTask} variant="contained">确定</Button>
        </DialogActions>
      </Dialog>

      {/* 删除任务确认对话框 */}
      <Dialog open={deleteTaskDialogOpen} onClose={() => setDeleteTaskDialogOpen(false)}>
        <DialogTitle>确认删除</DialogTitle>
        <DialogContent>
          <DialogContentText>
            确定要删除这个年度主要任务吗？此操作不可撤销。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTaskDialogOpen(false)}>取消</Button>
          <Button onClick={handleDeleteTask} variant="contained" color="error">
            删除
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI推荐对话框 */}
      <DepartmentIndicatorAIDialog
        open={aiDialogOpen}
        onClose={() => setAiDialogOpen(false)}
        project={project ? {
          id: project.id.toString(),
          project_name: project.project_name || '',
        } : null}
        onRecommendationComplete={handleAIRecommendationComplete}
      />

      {/* 排序对话框 */}
      <DepartmentIndicatorSortDialog
        open={sortDialogOpen}
        onClose={handleCloseSortDialog}
        indicators={sortedIndicators}
        onSave={handleSaveSort}
        isSaving={isSorting}
      />
    </>
  );
};

export default DepartmentIndicatorManager;
